import {
	Button,
	Checkbox,
	Chip,
	ChipConfirmModal,
	ChipSelect,
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
	EmptyState,
	Input,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@ondit/ui";
/**
 * Every prompt, with everything it produced.
 *
 * All five metrics on one row, because "which of my prompts is working" is not
 * a single number: a prompt with high visibility and no citation share is one
 * the assistants answer from memory, which is a completely different problem —
 * and a completely different piece of work — from one they never answer.
 *
 * The prompts are also *edited* here. Profound sends you to a separate editor;
 * this keeps the list and the editing in one place because the reason to add a
 * prompt is almost always a row you are looking at, and a screen you have to
 * leave to act on what it told you is a screen people stop acting on.
 *
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, Columns3, MoreHorizontal, Plus } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { useAccess, whyDisabled } from "~/core/access/index";
import { ApiError, api, METRICS, type MetricName, type PromptRow } from "~/core/api/index";
import { useStored } from "~/core/hooks";
import { useI18n, useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { Async } from "~/core/ui/states";
import { value } from "~/modules/analytics/components/chart";
import { Drill } from "~/modules/analytics/components/detail";
import { INTENTS } from "~/modules/analytics/components/filters";
import { type ReadingProps, StageShell } from "~/modules/analytics/components/stage";
import { matches, SortHeader, TableTools, useSort } from "~/modules/analytics/components/table";

type Column = "text" | "topic" | "runs" | MetricName;

/** Below this the fold buys nothing: the whole table is a screen or two. */
const FOLD_ABOVE = 25;

/**
 * Which metric columns are shown, kept per browser.
 *
 * Six metrics beside a prompt is a table that scrolls sideways on a laptop, and
 * the two anybody is working on this week are not the same two every week. A
 * viewing preference, so it is stored rather than put in the URL: nobody shares
 * it, and a link that arrived with somebody else's columns hidden would read as
 * missing data.
 */
const DEFAULT_COLUMNS: MetricName[] = ["visibility", "share_of_voice", "average_position", "citation_share"];

/** The value a row carries for one metric column. */
function metricOf(row: PromptRow, metric: MetricName): number | null {
	switch (metric) {
		case "visibility":
			return row.visibility;
		case "share_of_voice":
			return row.share_of_voice;
		case "average_position":
			return row.average_position;
		case "citation_share":
			return row.citation_share;
		case "sentiment":
			return row.sentiment;
	}
}

export function InsightPrompts({ entityId, filters, setFilters }: ReadingProps) {
	const t = useT();
	const { locale } = useI18n();
	const { editor, why } = useAccess();
	const [selectedPrompts, setSelectedPrompts] = useState<Set<string>>(new Set());
	const [sampling, setSampling] = useState(false);
	const [sampleQueued, setSampleQueued] = useState(false);
	async function sample(ids: string[]) {
		setSampling(true);
		try {
			const run = await api.sampleNow(entityId, filters, ids);
			setSelectedPrompts(new Set());
			setSampleQueued(true);
			toast.success(t("manual.sampleEstimate", { n: String(run.estimate_credits) }));
		} catch (e) {
			toast.error(e instanceof ApiError ? e.localized(locale) : String(e));
		} finally {
			setSampling(false);
		}
	}
	function selectPrompt(id: string) {
		setSelectedPrompts((current) => {
			const next = new Set(current);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	}
	const [query, setQuery] = useState("");
	const [grouped, setGrouped] = useState(true);
	const [open, setOpen] = useState<ReadonlySet<string> | null>(null);
	const [editing, setEditing] = useState<PromptRow | null>(null);
	const [adding, setAdding] = useState(false);
	const [removing, setRemoving] = useState<PromptRow | null>(null);
	// Comma-separated, because that is what a browser can store and it is the
	// same shape the topic filter travels in.
	const [stored, setStored] = useStored("ondit.promptColumns", DEFAULT_COLUMNS.join(","));
	const sort = useSort<Column>("runs");
	// A preference written by an older build, or edited by hand, must not take
	// the table down: anything that is not a metric this version knows is
	// dropped, and an empty set falls back rather than rendering a list of
	// prompts with no numbers beside them.
	const known = stored.split(",").filter((one): one is MetricName => METRICS.includes(one as MetricName));
	const picked = known.length > 0 ? known : DEFAULT_COLUMNS;
	const setColumns = (next: MetricName[]) => setStored(next.join(","));

	const searching = query.trim().length > 0;

	const queryClient = useQueryClient();
	const state = useQuery({
		queryKey: [
			...keys.entity(entityId),
			"insights",
			"prompts",
			{
				range: filters.range,
				engine: filters.engine,
				locale: filters.locale,
				topics: filters.topics,
				intent: filters.intent,
			},
		],
		queryFn: () => api.insights.prompts(entityId, filters),
		...read,
	});
	const drop = useMutation({
		mutationFn: (id: string) => api.removeQuestion(entityId, id),
		onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.entity(entityId) }),
	});

	async function remove(id: string) {
		try {
			await drop.mutateAsync(id);
		} catch (e) {
			toast.error(e instanceof ApiError ? e.localized(locale) : String(e));
		}
	}

	return (
		<StageShell
			entityId={entityId}
			reading="prompts"
			filters={filters}
			setFilters={setFilters}
			topics={state.data?.topics}
			actions={
				<Chip
					variant={adding ? undefined : "primary"}
					leftIcon={Plus}
					type="button"
					disabled={!editor}
					title={whyDisabled(editor, why.editor)}
					onClick={() => {
						setEditing(null);
						setAdding(!adding);
					}}
				>
					{adding ? t("common.cancel") : t("manual.addPrompt")}
				</Chip>
			}
		>
			<Async state={state}>
				{(data) => {
					const rows = sort.apply(
						data.prompts.filter((row) => matches(query, row.text, row.topic)),
						(row, column) =>
							column === "text"
								? row.text
								: column === "topic"
									? row.topic
									: column === "runs"
										? row.runs
										: metricOf(row, column),
					);

					const selectable = rows.filter((row) => row.scope === "private");
					const selected = selectable.filter((row) => selectedPrompts.has(row.id));
					const folded = rows.length > FOLD_ABOVE;
					const isOpen = (key: string) => searching || (open === null ? !folded : open.has(key));
					const toggle = (key: string) => {
						// The first click starts from whatever the table opened as, so
						// closing one group of an unfolded table leaves the rest open.
						const next = new Set(open ?? (folded ? [] : groupByTopic(rows).map(([topic]) => topic || "-")));
						if (!next.delete(key)) next.add(key);
						setOpen(next);
					};

					return (
						<>
							{/* Profound's header sentence, and it earns its place: it is
							    the one line that says what this account is actually
							    paying to measure every day. */}
							<div className="prompt-head">
								<p className="muted">
									{t("insights.promptSummary", {
										prompts: data.prompts.length,
										topics: data.topics.length,
										types: new Set(data.prompts.map((row) => row.intent)).size,
									})}
								</p>
							</div>

							{sampleQueued ? (
								<p role="status">
									{t("manual.sampleQueued")} <Link to={`/w/${entityId}/tasks`}>{t("manual.viewTasks")}</Link>
								</p>
							) : null}
							<p className="muted">{t("manual.sampleHelp")}</p>
							{adding ? (
								<AddPrompt
									key={editing?.id ?? "new"}
									editing={editing}
									entityId={entityId}
									filters={filters}
									onAdded={() => {
										setAdding(false);
										setEditing(null);
									}}
								/>
							) : null}

							<TableTools
								sort={sort.config([
									{ id: "text", label: t("visibility.question") },
									{ id: "topic", label: t("insights.topic") },
									{ id: "runs", label: t("insights.runs") },
									...picked.map((metric) => ({ id: metric, label: t(`metric.${metric}`) })),
								])}
								actions={
									<Button
										variant="default"
										disabled={!editor || sampling || selected.length === 0}
										onClick={() => sample(selected.map((row) => row.id))}
									>
										{t("manual.sampleSelected", { n: selected.length })}
									</Button>
								}
								query={query}
								onQuery={setQuery}
								placeholder={t("visibility.question")}
								shown={rows.length}
								total={data.prompts.length}
								csv={{ name: `prompts-${filters.range}`, rows: csv(rows) }}
							>
								{/* Six metrics beside a prompt is a table that scrolls
								    sideways on a laptop, and the two anybody is working on
								    this week are not the same two every week. */}
								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<Chip leftIcon={Columns3}>{t("filter.views")}</Chip>
									</DropdownMenuTrigger>
									<DropdownMenuContent align="end">
										<DropdownMenuCheckboxItem checked={grouped} onCheckedChange={setGrouped}>
											{t("insights.groupByTopic")}
										</DropdownMenuCheckboxItem>
										<DropdownMenuSeparator />
										<DropdownMenuLabel>{t("insights.columns")}</DropdownMenuLabel>
										{METRICS.map((metric) => (
											<DropdownMenuCheckboxItem
												key={metric}
												checked={picked.includes(metric)}
												// The last column cannot be turned off: a prompt
												// table with no metric in it is a list of prompts,
												// which is a different screen.
												disabled={picked.length === 1 && picked.includes(metric)}
												onCheckedChange={(on) =>
													setColumns(
														on
															? METRICS.filter((one) => one === metric || picked.includes(one))
															: picked.filter((one) => one !== metric),
													)
												}
											>
												{t(`metric.${metric}`)}
											</DropdownMenuCheckboxItem>
										))}
									</DropdownMenuContent>
								</DropdownMenu>
							</TableTools>

							{rows.length === 0 ? (
								<EmptyState size="compact" title={t("table.noMatch")} />
							) : (
								<div className="table-scroll">
									<Table variant="resource">
										<TableHeader>
											<TableRow>
												<TableHead>
													<Checkbox
														aria-label={t("manual.selectPrompts")}
														disabled={!editor || sampling || !selectable.length}
														checked={selected.length > 0 && selected.length === selectable.length}
														onCheckedChange={(on) =>
															setSelectedPrompts(on ? new Set(selectable.map((row) => row.id)) : new Set())
														}
													/>
												</TableHead>
												<SortHeader className="wrap" label={t("visibility.question")} column="text" sort={sort} />
												{grouped ? null : <SortHeader label={t("insights.topic")} column="topic" sort={sort} />}
												<SortHeader className="num" label={t("insights.runs")} column="runs" sort={sort} />
												{picked.map((metric) => (
													<SortHeader
														key={metric}
														className="num"
														label={t(`metric.${metric}`)}
														column={metric}
														sort={sort}
													/>
												))}
												<TableHead />
											</TableRow>
										</TableHeader>
										{grouped ? (
											groupByTopic(rows).map(([topic, group]) => {
												const key = topic || "-";
												// A search is its own expansion: somebody who typed a
												// word wants the rows that matched it, not the names of
												// the topics they are filed under.
												const shown = isOpen(key);
												return (
													<TableBody key={key}>
														<TableRow className="group-row">
															<TableHead colSpan={picked.length + 4}>
																<Button
																	variant="ghost"
																	type="button"
																	className="group-toggle h-auto w-full justify-start whitespace-normal px-0 text-left"
																	aria-expanded={shown}
																	onClick={() => toggle(key)}
																>
																	<ChevronRight className="group-caret" aria-hidden />
																	{topic || t("insights.noTopic")}
																	<span className="faint">
																		{t("insights.nPrompts", { n: group.length })} ·{" "}
																		{t("insights.nAnswers", {
																			n: group.reduce((sum, row) => sum + row.runs, 0),
																		})}
																	</span>
																</Button>
															</TableHead>
														</TableRow>
														{shown
															? group.map((row) => (
																	<Row
																		key={row.id}
																		row={row}
																		grouped
																		columns={picked}
																		editor={editor}
																		onEdit={() => {
																			setEditing(row);
																			setAdding(true);
																		}}
																		onRemove={() => setRemoving(row)}
																		selected={selectedPrompts.has(row.id)}
																		onSelect={() => selectPrompt(row.id)}
																		onSample={() => sample([row.id])}
																		sampling={sampling}
																	/>
																))
															: null}
													</TableBody>
												);
											})
										) : (
											<TableBody>
												{rows.map((row) => (
													<Row
														key={row.id}
														row={row}
														grouped={false}
														columns={picked}
														editor={editor}
														onEdit={() => {
															setEditing(row);
															setAdding(true);
														}}
														onRemove={() => setRemoving(row)}
														selected={selectedPrompts.has(row.id)}
														onSelect={() => selectPrompt(row.id)}
														onSample={() => sample([row.id])}
														sampling={sampling}
													/>
												))}
											</TableBody>
										)}
									</Table>
								</div>
							)}
						</>
					);
				}}
			</Async>

			{/* One dialog for the whole table, told which row it is about: a hundred
			    prompts used to mean a hundred mounted dialogs for a click that
			    happens once in a session. */}
			<ChipConfirmModal
				open={removing !== null}
				onOpenChange={(open) => (open ? null : setRemoving(null))}
				title={t("questions.removeTitle")}
				text={t("questions.removeBody")}
				dismissLabel={t("common.cancel")}
				confirm={{
					label: t("questions.remove"),
					variant: "destructive",
					onClick: () => {
						if (removing) remove(removing.id);
						setRemoving(null);
					},
				}}
			/>
		</StageShell>
	);
}

function Row({
	row,
	grouped,
	columns,
	editor,
	onEdit,
	onRemove,
	selected,
	onSelect,
	onSample,
	sampling,
}: {
	row: PromptRow;
	grouped: boolean;
	columns: MetricName[];
	editor: boolean;
	onEdit: () => void;
	onRemove: () => void;
	selected: boolean;
	onSelect: () => void;
	onSample: () => void;
	sampling: boolean;
}) {
	const t = useT();
	const { locale } = useI18n();
	return (
		<TableRow>
			<TableCell>
				{row.scope === "private" ? (
					<Checkbox
						aria-label={t("manual.selectPrompt", { text: row.text })}
						checked={selected}
						onCheckedChange={onSelect}
						disabled={!editor || sampling}
					/>
				) : null}
			</TableCell>
			<TableCell className="wrap">
				{/* The prompt opens onto the answers it got: this table says whether
				    a prompt is working, and the next thing anybody asks is what was
				    said instead. */}
				<Drill view={{ kind: "answers", label: row.text, filters: { question: row.id } }}>{row.text}</Drill>
				<span className="pill pill-quiet">{t(`questions.intent.${row.intent}`)}</span>
			</TableCell>
			{grouped ? null : <TableCell>{row.topic || <span className="faint">{t("insights.noTopic")}</span>}</TableCell>}
			<TableCell className="num mono">{row.runs}</TableCell>
			{columns.map((metric) => (
				<TableCell className="num mono" key={metric}>
					{value(locale, metric, metricOf(row, metric))}
					{/* The rank inside this one prompt, where there is one: the figure
					    says how we did, the rank says whether anybody did better. */}
					{metric === "visibility" && row.visibility_rank ? (
						<span className="faint"> #{row.visibility_rank}</span>
					) : null}
					{metric === "citation_share" && row.citation_rank ? (
						<span className="faint"> #{row.citation_rank}</span>
					) : null}
				</TableCell>
			))}
			<TableCell className="num">
				{row.scope === "private" ? (
					<div className="record-actions">
						<Button variant="default" disabled={!editor || sampling} onClick={onEdit}>
							{t("manual.editPrompt")}
						</Button>
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button variant="default" size="icon" aria-label={t("table.moreActions")}>
									<MoreHorizontal />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								<DropdownMenuItem disabled={!editor || sampling} onSelect={onSample}>
									{t("overview.sampleNow")}
								</DropdownMenuItem>
								<DropdownMenuItem className="text-[var(--text-error)]" disabled={!editor} onSelect={onRemove}>
									{t("common.remove")}
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					</div>
				) : null}
			</TableCell>
		</TableRow>
	);
}

/**
 * Adding a prompt, and filing it.
 *
 * The topic is on the form rather than a second step: a prompt added without
 * one lands in the "not filed" bucket on every topic screen, and the moment to
 * decide what it is about is the moment somebody is writing it.
 */
function AddPrompt({
	editing,
	entityId,
	filters,
	onAdded,
}: {
	entityId: string;
	filters: { locale: string };
	onAdded: () => void;
	editing: PromptRow | null;
}) {
	const t = useT();
	const { locale } = useI18n();
	const [text, setText] = useState(editing?.text ?? "");
	const [topic, setTopic] = useState(editing?.topic ?? "");
	const [intent, setIntent] = useState<string>(editing?.intent ?? "informational");
	const [busy, setBusy] = useState(false);
	const { editor } = useAccess();
	const queryClient = useQueryClient();
	// The question and its topic are one change to somebody adding a prompt,
	// so they are one write here.
	const write = useMutation({
		mutationFn: async (next: { text: string; intent: string; topic: string }) => {
			const { id } = editing
				? await api.editQuestion(entityId, editing.id, { text: next.text, intent: next.intent })
				: await api.addQuestion(entityId, { text: next.text, intent: next.intent, locale: filters.locale });
			await api.setTopic(entityId, id, next.topic);
		},
		onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.entity(entityId) }),
	});

	async function add(event: React.FormEvent) {
		event.preventDefault();
		setBusy(true);
		try {
			await write.mutateAsync({ text, intent, topic: topic.trim() });
			setText("");
			onAdded();
		} catch (e) {
			toast.error(e instanceof ApiError ? e.localized(locale) : String(e));
		} finally {
			setBusy(false);
		}
	}

	return (
		<form className="panel" onSubmit={add} style={{ marginBottom: "var(--s-10)" }}>
			<fieldset className="bare-fieldset" disabled={!editor || busy}>
				{editing ? <p className="muted">{t("manual.editHelp")}</p> : null}
				<div className="row-actions">
					<Input
						className="w-full min-w-0 sm:w-auto sm:flex-1"
						aria-label={t("visibility.question")}
						placeholder={t("questions.placeholder")}
						value={text}
						onChange={(event) => setText(event.target.value)}
						minLength={8}
						maxLength={300}
						required
					/>
					<Input
						className="max-w-40"
						aria-label={t("insights.topic")}
						placeholder={t("insights.topic")}
						value={topic}
						maxLength={60}
						onChange={(event) => setTopic(event.target.value)}
					/>
					<ChipSelect
						value={intent}
						onChange={setIntent}
						aria-label={t("questions.intent")}
						options={INTENTS.map((one) => ({ value: one, label: t(`questions.intent.${one}`) }))}
					/>
					<Button type="submit" disabled={busy}>
						{editing ? t("common.save") : t("questions.add")}
					</Button>
				</div>
			</fieldset>
		</form>
	);
}

function groupByTopic(rows: PromptRow[]): [string, PromptRow[]][] {
	const groups = new Map<string, PromptRow[]>();
	for (const row of rows) {
		const list = groups.get(row.topic) ?? [];
		list.push(row);
		groups.set(row.topic, list);
	}
	// Filed topics first, alphabetically; the unfiled bucket last, because it is
	// a to-do list rather than a subject.
	return [...groups.entries()].sort(([a], [b]) => (a === "" ? 1 : b === "" ? -1 : a.localeCompare(b)));
}

function csv(rows: PromptRow[]): (string | number)[][] {
	return [
		[
			"prompt",
			"topic",
			"intent",
			"runs",
			"visibility",
			"share_of_voice",
			"average_position",
			"citation_share",
			"sentiment",
		],
		...rows.map((row) => [
			row.text,
			row.topic,
			row.intent,
			row.runs,
			row.visibility?.toFixed(1) ?? "",
			row.share_of_voice?.toFixed(1) ?? "",
			row.average_position?.toFixed(1) ?? "",
			row.citation_share?.toFixed(1) ?? "",
			row.sentiment?.toFixed(1) ?? "",
		]),
	];
}
